/**
 * The rate table (ARCHITECTURE.md §5.4; BUILDLIST 64.2): the systems that run below the tick rate,
 * each with a period in ticks and a phase. `isDue(rate, tick)` is the one test every such system makes.
 *
 * Rates are part of the ruleset and fixed — never per machine. Today every phase is 0, which is what
 * the systems did before the table existed, so nothing moves. Staggering the phases by id so no side
 * always looks first (59.4's lesson) changes results, so it is a later, versioned step.
 *
 * Declared elsewhere, listed here so the table is complete: the action bots' perception period
 * (`ai/actionBot.ts` SIGHT_PERIOD, 24 ticks — lane C's), and the turn clock's per-turn and per-round
 * rules, which 64.4 gives a family.
 */
import type { GameState } from '../types';
import { PHYS_DT } from '../constants';
import { TOWER_SIGHT_PERIOD } from '../core/towers';
import { personSetOf } from '../core/scaleProfile';

export interface Rate {
  /** Ticks between runs. */
  period: number;
  /** The tick within the period on which the system runs. */
  phase: number;
}

/** Does a system with this rate run on this tick? */
export const isDue = (rate: Rate, tick: number): boolean => tick % rate.period === rate.phase;

/** The rates that don't depend on the match: towers and drones look a few times a second (as the bots do). */
export const RATES = {
  towerSight: { period: TOWER_SIGHT_PERIOD, phase: 0 } as Rate,
  droneSight: { period: TOWER_SIGHT_PERIOD, phase: 0 } as Rate,
  relaySight: { period: TOWER_SIGHT_PERIOD, phase: 0 } as Rate,   // 69.2b: a relay looks as a tower does, each on its id's tick of the beat (match.ts)
  web: { period: Math.round(1 / PHYS_DT), phase: 0 } as Rate,
  plays: { period: Math.round(1 / PHYS_DT), phase: 0 } as Rate,   // 68.22: the plays' phases, once a second (core/plays.ts)   // 69.2c: each side's web, worked out once a second (core/web.ts)
  structureCapture: { period: Math.round(0.1 / PHYS_DT), phase: 0 } as Rate,   // 69.4 step 4: a relay's or FOB's capture clock, ten times a second (core/structureCapture.ts)
  convoys: { period: Math.round(1 / PHYS_DT), phase: 0 } as Rate,
  rearm: { period: Math.round(0.5 / PHYS_DT), phase: 0 } as Rate,   // 69.30 step 1: a parked vehicle's rearm at a dump, twice a second (core/munitions.ts; the paces are its multiples)   // 69.4 step 5: the convoys' beat — the trucks' cargo, the runs, the calls — once a second (core/convoys.ts)
};

/** The respawn wave's rate: its period is a person-set number (seconds), so it depends on the match's scale. */
export function respawnWaveRate(state: GameState): Rate {
  return { period: Math.round(personSetOf(state).respawnWave / PHYS_DT), phase: 0 };
}
